import React, { useId, useState } from "react";

/**
 * Shared chart chrome for the dashboards.
 *
 * The categorical slots below are used in fixed order and were validated
 * against the white card surface (lightness band, chroma floor, CVD separation,
 * normal-vision floor). Slot 3 sits just under 3:1 contrast, which is why every
 * chart built with this module ships a table view - values are never reachable
 * by colour alone.
 */
export const VIZ = {
  surface: "#ffffff",
  ink: "#0b0b0b",
  inkSecondary: "#52514e",
  inkMuted: "#898781",
  grid: "#e1e0d9",
  axis: "#c3c2b7",
  border: "rgba(11, 11, 11, 0.10)",
  deemphasis: "#c3c2b7",
  // Categorical slots - assigned in order, never cycled.
  series: [
    "#2a78d6",
    "#eb6834",
    "#1baf7a",
    "#eda100",
    "#e87ba4",
    "#008300",
    "#4a3aa7",
    "#e34948",
  ],
  status: {
    good: "#0ca30c",
    warning: "#fab219",
    serious: "#ec835a",
    critical: "#d03b3b",
  },
  successText: "#006300",
};

export const FONT =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/* ---------------------------------------------------------------- formatters */

export const money = (value) =>
  "৳" +
  Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

export const compactMoney = (value) => "৳" + compact(value);

export function compact(value) {
  const n = Number(value || 0);
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs >= 10000000) return sign + (abs / 10000000).toFixed(1) + "Cr";
  if (abs >= 100000) return sign + (abs / 100000).toFixed(1) + "L";
  if (abs >= 1000) return sign + (abs / 1000).toFixed(1) + "K";
  return sign + String(Math.round(abs * 100) / 100);
}

export const number = (value) =>
  Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

/* ------------------------------------------------------------- axis defaults */

// Recessive, solid hairline chrome. Never dashed.
export const axisProps = {
  tickLine: false,
  axisLine: { stroke: VIZ.axis },
  tick: { fill: VIZ.inkMuted, fontSize: 11 },
};

export const gridProps = {
  stroke: VIZ.grid,
  strokeDasharray: "",
  vertical: false,
};

/* ------------------------------------------------------------------ tooltip */

/**
 * One tooltip listing every series at that x. The value leads and the series
 * name follows, keyed by a short stroke of the series colour rather than a
 * filled box. Text never wears the data colour.
 */
export const VizTooltip = ({ active, payload, label, format = number }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div
      style={{
        background: VIZ.surface,
        border: `1px solid ${VIZ.border}`,
        borderRadius: "6px",
        boxShadow: "0 4px 16px rgba(11,11,11,0.10)",
        padding: "10px 12px",
        fontFamily: FONT,
        minWidth: "160px",
      }}
    >
      <div
        style={{
          color: VIZ.inkMuted,
          fontSize: "11px",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: "6px",
        }}
      >
        {label}
      </div>
      {payload.map((entry) => (
        <div
          key={entry.dataKey || entry.name}
          className="d-flex align-items-center justify-content-between"
          style={{ gap: "16px", marginTop: "3px" }}
        >
          <span className="d-flex align-items-center" style={{ gap: "6px" }}>
            <span
              style={{
                width: "12px",
                height: "2px",
                borderRadius: "1px",
                background: entry.color,
                flexShrink: 0,
              }}
            />
            <span style={{ color: VIZ.inkSecondary, fontSize: "12px" }}>
              {entry.name}
            </span>
          </span>
          <strong style={{ color: VIZ.ink, fontSize: "13px" }}>
            {format(entry.value)}
          </strong>
        </div>
      ))}
    </div>
  );
};

/* -------------------------------------------------------------- delta badge */

/**
 * Change against the previous period. Direction is carried by an arrow glyph
 * and the signed number, so the colour is never the only cue.
 */
export const DeltaBadge = ({ change, goodWhenUp = true, note }) => {
  if (change === null || change === undefined) {
    return note ? (
      <span style={{ color: VIZ.inkMuted, fontSize: "11px" }}>{note}</span>
    ) : null;
  }

  const up = change >= 0;
  const good = up === goodWhenUp;
  const color = change === 0 ? VIZ.inkMuted : good ? VIZ.successText : VIZ.status.critical;

  return (
    <span
      className="d-inline-flex align-items-center"
      style={{ gap: "4px", color, fontSize: "11px", fontWeight: 600 }}
    >
      <i className={`fa fa-arrow-${up ? "up" : "down"}`} aria-hidden="true" />
      {Math.abs(change).toFixed(1)}%
      {note ? (
        <span style={{ color: VIZ.inkMuted, fontWeight: 400 }}>{note}</span>
      ) : null}
    </span>
  );
};

/* ---------------------------------------------------------------- stat tile */

export const StatTile = ({ label, value, sub, change, goodWhenUp = true, accent }) => (
  <div
    style={{
      background: VIZ.surface,
      border: `1px solid ${VIZ.border}`,
      borderRadius: "10px",
      padding: "16px 18px",
      height: "100%",
      fontFamily: FONT,
    }}
  >
    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
      {accent ? (
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "2px",
            background: accent,
            flexShrink: 0,
          }}
        />
      ) : null}
      <span
        style={{
          color: VIZ.inkSecondary,
          fontSize: "12px",
          letterSpacing: "0.01em",
        }}
      >
        {label}
      </span>
    </div>
    <div
      style={{
        color: VIZ.ink,
        fontSize: "26px",
        fontWeight: 600,
        lineHeight: 1.15,
        marginTop: "6px",
      }}
    >
      {value}
    </div>
    <div
      className="d-flex align-items-center flex-wrap"
      style={{ gap: "8px", marginTop: "4px", minHeight: "16px" }}
    >
      <DeltaBadge change={change} goodWhenUp={goodWhenUp} />
      {sub ? (
        <span style={{ color: VIZ.inkMuted, fontSize: "11px" }}>{sub}</span>
      ) : null}
    </div>
  </div>
);

/* --------------------------------------------------------------- chart card */

const TableView = ({ rows, columns }) => (
  <div className="table-responsive" style={{ maxHeight: "320px" }}>
    <table
      className="table table-sm mg-b-0"
      style={{ fontFamily: FONT, fontSize: "12px" }}
    >
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              className={col.align === "right" ? "tx-right" : ""}
              style={{ color: VIZ.inkSecondary, fontWeight: 600 }}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
        {rows.map((row, index) => (
          <tr key={index}>
            {columns.map((col) => (
              <td
                key={col.key}
                className={col.align === "right" ? "tx-right" : ""}
                style={{ color: VIZ.ink }}
              >
                {col.format ? col.format(row[col.key], row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * A chart container: title, optional note, the chart itself, and the table view
 * that every chart here is required to have. `stale` dims the previous render
 * during a refetch instead of collapsing to a skeleton.
 */
export const ChartCard = ({
  title,
  note,
  rows = [],
  columns,
  children,
  stale = false,
  empty = "No data in this range.",
  // Only the empty state uses this - a message should not reserve a plot's height.
  minHeight = 150,
}) => {
  const [asTable, setAsTable] = useState(false);
  const id = useId();
  const hasData = rows.length > 0;

  return (
    <figure
      className="mg-b-0"
      style={{
        background: VIZ.surface,
        border: `1px solid ${VIZ.border}`,
        borderRadius: "10px",
        padding: "18px 18px 12px",
        height: "100%",
        fontFamily: FONT,
        margin: 0,
      }}
    >
      <figcaption className="d-flex justify-content-between align-items-start mg-b-15">
        <div>
          <h6
            style={{
              color: VIZ.ink,
              fontSize: "14px",
              fontWeight: 600,
              margin: 0,
            }}
          >
            {title}
          </h6>
          {note ? (
            <p
              style={{
                color: VIZ.inkMuted,
                fontSize: "11px",
                margin: "3px 0 0 0",
              }}
            >
              {note}
            </p>
          ) : null}
        </div>
        {columns && hasData ? (
          <button
            type="button"
            className="btn btn-sm"
            aria-pressed={asTable}
            aria-controls={id}
            onClick={() => setAsTable((prev) => !prev)}
            style={{
              border: `1px solid ${VIZ.border}`,
              borderRadius: "6px",
              color: VIZ.inkSecondary,
              fontSize: "11px",
              padding: "3px 10px",
              background: asTable ? "#f2f4f7" : VIZ.surface,
              whiteSpace: "nowrap",
            }}
          >
            <i
              className={`fa fa-${asTable ? "chart-bar" : "table"}`}
              aria-hidden="true"
            />{" "}
            {asTable ? "Chart" : "Table"}
          </button>
        ) : null}
      </figcaption>

      <div
        id={id}
        style={{
          opacity: stale ? 0.45 : 1,
          transition: "opacity 0.2s ease",
        }}
      >
        {!hasData ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight, color: VIZ.inkMuted, fontSize: "13px" }}
          >
            {empty}
          </div>
        ) : asTable ? (
          <TableView rows={rows} columns={columns} />
        ) : (
          children
        )}
      </div>
    </figure>
  );
};

/* -------------------------------------------------------------- legend text */

// Recharts colours legend text with the series colour by default; text wears
// text tokens instead, with the coloured key beside it carrying identity.
export const legendProps = {
  wrapperStyle: { fontSize: "12px", paddingTop: "8px" },
  formatter: (value) => (
    <span style={{ color: VIZ.inkSecondary, fontFamily: FONT }}>{value}</span>
  ),
};
